<script lang="ts">
  import { onMount } from 'svelte'
  import { apiBaseUrl } from '$lib/api/base-url'
  import type { Prefill } from '$lib/public/wizard-state'
  import Wizard from '../public/Wizard.svelte'

  interface Props {
    token: string
  }
  const { token }: Props = $props()

  type Scope = {
    propertyManagerId: number
    propertyId: number | null
    pmSlug: string | null
    propertySlug: string | null
  }
  type Resolved = {
    status: string
    scope: Scope | null
    prefill: Prefill | null
    allowedFrameOrigins: string[]
  }

  type View =
    | { kind: 'loading' }
    | { kind: 'wizard'; scope: Scope; prefill: Prefill | undefined }
    | { kind: 'terminal'; message: string }

  let view = $state<View>({ kind: 'loading' })

  // The parent (framing) origin. ancestorOrigins is the trustworthy source when the
  // page is framed; document.referrer is a best-effort fallback for browsers lacking it.
  const framingOrigin = ((): string | null => {
    const ancestor = window.location.ancestorOrigins?.[0]
    if (ancestor) return ancestor
    if (document.referrer) {
      try {
        return new URL(document.referrer).origin
      } catch {
        return null
      }
    }
    return null
  })()

  const apiHost = new URL(apiBaseUrl()).hostname
  const isLocalDev = apiHost === 'localhost' || apiHost === '127.0.0.1'

  // Where postMessage is allowed to deliver. Prefer the concrete framing origin so
  // we never broadcast to '*'; only the dev-localhost allowance may fall back to '*'.
  const targetOrigin: string | null = framingOrigin ?? (isLocalDev ? '*' : null)

  function post(message: Record<string, unknown>): void {
    if (!targetOrigin) return
    window.parent.postMessage({ source: 'rrp-embed', ...message }, targetOrigin)
  }

  function onComplete(): void {
    // The public resolve does not return the session id; the parent can correlate by
    // the token it originally issued. Send success without a sessionId.
    post({ type: 'success' })
  }

  onMount(() => {
    let observer: ResizeObserver | null = null

    void (async () => {
      let resolved: Resolved
      try {
        const res = await fetch(`${apiBaseUrl()}/public/screening-sessions/${encodeURIComponent(token)}`)
        resolved = (await res.json()) as Resolved
      } catch {
        view = { kind: 'terminal', message: 'This screening link could not be loaded.' }
        post({ type: 'exit' })
        return
      }

      if (resolved.status !== 'CREATED' || !resolved.scope) {
        view = { kind: 'terminal', message: 'This screening link has expired or was already used.' }
        post({ type: 'exit' })
        return
      }

      // Framing check: the parent origin must be on the partner's allowlist. In local
      // dev, allow an unframed page (no ancestorOrigins) so the route can be opened directly.
      const framingAllowed = framingOrigin
        ? resolved.allowedFrameOrigins.includes(framingOrigin)
        : isLocalDev
      if (!framingAllowed) {
        view = { kind: 'terminal', message: 'This screening cannot be displayed here.' }
        post({ type: 'exit' })
        return
      }

      view = { kind: 'wizard', scope: resolved.scope, prefill: resolved.prefill ?? undefined }
      post({ type: 'ready' })

      observer = new ResizeObserver(() => {
        post({ type: 'resize', height: document.body.scrollHeight })
      })
      observer.observe(document.body)
    })()

    return () => observer?.disconnect()
  })
</script>

{#if view.kind === 'wizard'}
  <div class="embed-shell">
    <Wizard
      pmSlug={view.scope.pmSlug ?? undefined}
      propertySlug={view.scope.propertySlug ?? undefined}
      sessionToken={token}
      prefill={view.prefill}
      {onComplete}
    />
  </div>
{:else if view.kind === 'terminal'}
  <div class="embed-shell">
    <p class="embed-terminal">{view.message}</p>
  </div>
{/if}

<style>
  .embed-shell {
    padding: 16px;
    max-width: 720px;
    margin: 0 auto;
    box-sizing: border-box;
  }
  .embed-terminal {
    color: var(--muted);
    font-size: 0.95rem;
  }
</style>
